import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import React, {useEffect, useMemo, useState} from "react";
import {permissionObj} from "../../../../Helpers/permission";
import {DeleteRelationshipsWithDevices} from "../../../../reduxToolkit/Commons/CommonsApi";
import {errorMsg, getFullName} from "../../../../Apis/functions";
import {TABS_FA} from "../Model/Model";
import SelectedDepartmentTotalEmployeesCard
    from "../../../../components/CustomAvailableAssignedItems/SubComponents/SelectedDepartmentTotalEmployeesCard";
import {TABLES} from "../../../../Apis/Tables";
import {toast} from "react-toastify";

/**
 * Custom hook used to manage assigned employees or departments.
 *
 * This hook handles:
 * - Delete permissions validation.
 * - Delete modal visibility.
 * - Assigned item rendering.
 * - Relationship deletion logic.
 * - Dynamic configuration generation depending on the current subview.
 *
 * @function AssignedEmployeeApp
 *
 * @param {Object} props - Hook parameters.
 * @param {Array<number|string>} props.selected - Selected item identifiers.
 * @param {Function} props.setSelected - Updates selected item identifiers.
 * @param {Function} props.setModified - Updates modified state after successful deletion.
 * @param {string} props.subView - Current selected tab/subview.
 * @param {boolean} props.triggerDelete - Flag used to trigger delete action externally.
 * @param {Function} props.setTriggerDelete - Updates delete trigger state.
 * @param {Function} props.setIsAllChecked - Updates global checked state.
 *
 * @returns {Object} Hook response object.
 * @returns {Function} returns.t - Translation function from react-i18next.
 * @returns {boolean} returns.deletePermission - Indicates whether delete action is allowed.
 * @returns {Function} returns.handleDelete - Handles delete modal opening.
 * @returns {Function} returns.onDelete - Executes delete request.
 * @returns {Function} returns.hide - Closes delete modal and resets selected items.
 * @returns {boolean} returns.deleteModalShow - Delete modal visibility state.
 * @returns {Object} returns.dynamicConfig - Dynamic configuration object depending on subview.
 */
export const AssignedEmployeeApp = ({ selected, setSelected, setModified,subView,triggerDelete,
                                        setTriggerDelete,setIsAllChecked }) => {
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const deletePermission = DEV || permission.includes(permissionObj?.WEB_DEVICE_DELETE);

    /**
     * Renders assigned department items.
     *
     * @param {Object} params - Render parameters.
     * @param {Object} params.item - Current item to render.
     * @param {string} params.keyId - Dynamic item identifier key.
     * @param {Function} params.onDelete - Callback executed when deleting an item.
     * @param {boolean} params.checkbox - Indicates whether checkbox support is enabled.
     * @param {Function} params.handleCheckboxChange - Checkbox change handler.
     *
     * @returns {JSX.Element} Rendered assigned item component.
     */

    const renderAssignedItem =  ({ item, keyId, onDelete, checkbox, handleCheckboxChange }) => (
        <SelectedDepartmentTotalEmployeesCard
            keyId={keyId}
            item={{
                [keyId]: item[keyId],
                fullName: item?.fullName || getFullName(item?.name, item?.firstLastName, item?.secondLastName),
                totalEmployees : item?.totalEmployees,
                disabled: false,
                checked: selected.includes(item[keyId])
            }}
            onDelete={onDelete}
            handleCheckboxChange={checkbox ? handleCheckboxChange : undefined}
        />
    );

    /**
     * Generates a dynamic configuration object depending on the selected subview.
     *
     * @type {Object}
     */
    const dynamicConfig = useMemo(() => {
        if (subView === TABS_FA.CUSTOM_ACCESS.key) {
            return {
                labelRowsPerPage: 'employees_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_employee_yet',
                keyId: 'id',
                title_modal: 'remove_employees',
                element_modal:'employees',
                searchTitle:'search_by_name_email_or_phone',
                model : TABLES.DEVICE_CUSTOM_ACCESS

            };
        }

        if (subView ===  TABS_FA.DEPARTMENTS.key) {
            return {
                labelRowsPerPage: 'departments_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_department_yet',
                keyId: 'id',
                title_modal: 'remove_departments',
                element_modal: 'departments',
                searchTitle:'search_by_name',
                model : TABLES.DEVICE_DEPARTMENT,
                renderAssignedItem : renderAssignedItem
            };
        }
        return {};
    }, [subView]);

    useEffect(() => {
        if (triggerDelete === true) {
            handleDelete();
            setTriggerDelete(false)
        }
    }, [triggerDelete]);

    const handleDelete = (item) => {
        if (item) setSelected([item[dynamicConfig.keyId]]);
        setDeleteModalShow(true);
    };

    const hide = () => {
        setSelected([]);
        setDeleteModalShow(false);
    };

    const onDelete = () => {
        if(selected.length > 0 ){
            dispatch(DeleteRelationshipsWithDevices({ option: dynamicConfig.model , body: selected }))
                .then(({ payload: { data: { code, success} } }) => {
                    if (success) {
                        setModified(true);
                        toast.success(t('has_been_deleted_successfully'));
                        hide();
                    } else {
                        errorMsg(code);
                    }
                });
        }
    }

    return {
        t,
        deletePermission,
        handleDelete,
        onDelete,
        hide,
        deleteModalShow,
        dynamicConfig
    }
}
